/**
 * Theme configuration for Penticton.dev Events platform
 *
 * Creates Material UI themes using design tokens and provides
 * utilities for theme consumption and color mode management.
 */

import { PaletteMode } from '@mui/material';
import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

import { tokens, lightPalette, darkPalette, getTransition } from './tokens';

// Extend Material UI theme interface with custom tokens and enhanced palette
declare module '@mui/material/styles' {
  interface Theme {
    customTokens: typeof tokens;
  }

  interface ThemeOptions {
    customTokens?: typeof tokens;
  }

  // Extend the Palette interface to include surface and elevated backgrounds
  interface TypeBackground {
    default: string;
    paper: string;
    surface: string; // For cards, chips
    elevated: string; // For elevated surfaces
  }

  interface PaletteOptions {
    background?: Partial<TypeBackground>;
  }
}

/**
 * Gets the base theme configuration for both light and dark modes
 */
const getBaseTheme = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === 'light' ? lightPalette : darkPalette),
    success: {
      light: tokens.colors.success.light,
      main: mode === 'light' ? tokens.colors.success[600] : tokens.colors.success[300],
      dark: tokens.colors.success.dark,
      contrastText: mode === 'light' ? '#ffffff' : tokens.colors.neutral[900],
    },
    warning: {
      light: tokens.colors.warning.light,
      main: mode === 'light' ? tokens.colors.warning[600] : tokens.colors.warning[300],
      dark: tokens.colors.warning.dark,
      contrastText: mode === 'light' ? '#ffffff' : tokens.colors.neutral[900],
    },
    error: {
      light: tokens.colors.error.light,
      main: mode === 'light' ? tokens.colors.error[600] : tokens.colors.error[300],
      dark: tokens.colors.error.dark,
      contrastText: mode === 'light' ? '#ffffff' : tokens.colors.neutral[900],
    },
    info: {
      light: tokens.colors.info.light,
      main: mode === 'light' ? tokens.colors.info[600] : tokens.colors.info[300],
      dark: tokens.colors.info.dark,
      contrastText: mode === 'light' ? '#ffffff' : tokens.colors.neutral[900],
    },
  },
  typography: {
    fontFamily: tokens.typography.fontFamily.sans,
    h1: {
      fontSize: tokens.typography.fontSize['5xl'],
      fontWeight: tokens.typography.fontWeight.bold,
      lineHeight: tokens.typography.lineHeight.tight,
    },
    h2: {
      fontSize: tokens.typography.fontSize['4xl'],
      fontWeight: tokens.typography.fontWeight.bold,
      lineHeight: tokens.typography.lineHeight.tight,
    },
    h3: {
      fontSize: tokens.typography.fontSize['3xl'],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.snug,
    },
    h4: {
      fontSize: tokens.typography.fontSize['2xl'],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.snug,
    },
    h5: {
      fontSize: tokens.typography.fontSize.xl,
      fontWeight: tokens.typography.fontWeight.medium,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    h6: {
      fontSize: tokens.typography.fontSize.lg,
      fontWeight: tokens.typography.fontWeight.medium,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    body1: {
      fontSize: tokens.typography.fontSize.base,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    body2: {
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    subtitle1: {
      fontSize: tokens.typography.fontSize.base,
      fontWeight: tokens.typography.fontWeight.medium,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    subtitle2: {
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.medium,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    caption: {
      fontSize: tokens.typography.fontSize.xs,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    overline: {
      fontSize: tokens.typography.fontSize.xs,
      fontWeight: tokens.typography.fontWeight.medium,
      textTransform: 'uppercase',
      letterSpacing: tokens.typography.letterSpacing.wide,
    },
    button: {
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.medium,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8, // tokens.radii.lg in pixels
  },
  spacing: (factor: number) => `${factor * 4}px`, // 4px base unit
  shadows: [
    'none',
    tokens.shadows.sm,
    tokens.shadows.base,
    tokens.shadows.md,
    tokens.shadows.md,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
  ],
  breakpoints: {
    values: {
      xs: parseInt(tokens.breakpoints.xs),
      sm: parseInt(tokens.breakpoints.sm),
      md: parseInt(tokens.breakpoints.md),
      lg: parseInt(tokens.breakpoints.lg),
      xl: parseInt(tokens.breakpoints.xl),
    },
  },
  components: {
    // Button customizations
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => {
          const isDark = theme.palette.mode === 'dark';
          return {
            borderRadius: tokens.radii.lg,
            textTransform: 'none',
            fontWeight: tokens.typography.fontWeight.medium,
            transition: getTransition(),
            '&:hover': {
              transform: getTransition() === 'none' ? 'none' : 'translateY(-1px)',
              boxShadow: tokens.shadows.md,
            },
            // Enhanced contrast in dark mode
            ...(isDark && {
              '&.MuiButton-containedPrimary': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }),
          };
        },
        sizeLarge: {
          padding: `${tokens.spacing[3]} ${tokens.spacing[8]}`,
          fontSize: tokens.typography.fontSize.base,
        },
        sizeMedium: {
          padding: `${tokens.spacing[2]} ${tokens.spacing[6]}`,
          fontSize: tokens.typography.fontSize.sm,
        },
        sizeSmall: {
          padding: `${tokens.spacing[1]} ${tokens.spacing[4]}`,
          fontSize: tokens.typography.fontSize.xs,
        },
      },
    },
    // Card customizations
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: tokens.radii.xl,
          boxShadow: tokens.shadows.base,
          backgroundColor: theme.palette.background.paper,
          transition: getTransition(),
          '&:hover': {
            boxShadow: tokens.shadows.lg,
            transform: getTransition() === 'none' ? 'none' : 'translateY(-2px)',
          },
        }),
      },
    },
    // Chip customizations for better dark mode contrast
    MuiChip: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const isDark = theme.palette.mode === 'dark';
          return {
            borderRadius: tokens.radii.full,
            fontWeight: tokens.typography.fontWeight.medium,
            // Enhanced backgrounds for dark mode
            ...(isDark && {
              '&.MuiChip-colorDefault': {
                backgroundColor: theme.palette.background.surface,
                color: theme.palette.text.primary,
              },
              '&.MuiChip-colorPrimary': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
              '&.MuiChip-colorSecondary': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
              },
              '&.MuiChip-colorSuccess': {
                backgroundColor: theme.palette.success.main,
                color: theme.palette.success.contrastText,
              },
              '&.MuiChip-colorWarning': {
                backgroundColor: theme.palette.warning.main,
                color: theme.palette.warning.contrastText,
              },
              '&.MuiChip-colorError': {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.error.contrastText,
              },
            }),
          };
        },
      },
    },
    // Paper customizations for better surfaces
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: tokens.radii.lg,
          backgroundColor: theme.palette.background.paper,
        }),
        rounded: {
          borderRadius: tokens.radii.xl,
        },
        elevation1: ({ theme }) => ({
          backgroundColor: theme.palette.background.surface,
          boxShadow: tokens.shadows.sm,
        }),
        elevation2: ({ theme }) => ({
          backgroundColor: theme.palette.background.elevated,
          boxShadow: tokens.shadows.base,
        }),
      },
    },
    // AppBar customizations
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: tokens.shadows.sm,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    // Container customizations
    MuiContainer: {
      styleOverrides: {
        maxWidthSm: {
          maxWidth: tokens.containers.sm,
        },
        maxWidthMd: {
          maxWidth: tokens.containers.md,
        },
        maxWidthLg: {
          maxWidth: tokens.containers.lg,
        },
        maxWidthXl: {
          maxWidth: tokens.containers.xl,
        },
      },
    },
  },
  // Include custom tokens in theme
  customTokens: tokens,
});

/**
 * Creates a Material UI theme with custom tokens
 */
export const createAppTheme = (
  mode: PaletteMode,
  reducedMotion: boolean = false,
): Theme => {
  const baseTheme = getBaseTheme(mode);

  // Override transitions if reduced motion is preferred
  if (reducedMotion) {
    baseTheme.components = {
      ...baseTheme.components,
      MuiButton: {
        ...baseTheme.components?.MuiButton,
        styleOverrides: {
          ...baseTheme.components?.MuiButton?.styleOverrides,
          root: ({ theme }) => {
            const isDark = theme.palette.mode === 'dark';
            return {
              borderRadius: tokens.radii.lg,
              textTransform: 'none',
              fontWeight: tokens.typography.fontWeight.medium,
              transition: 'none', // No transitions for reduced motion
              '&:hover': {
                transform: 'none', // No transforms for reduced motion
                boxShadow: tokens.shadows.md,
              },
              ...(isDark && {
                '&.MuiButton-containedPrimary': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
              }),
            };
          },
        },
      },
      MuiCard: {
        ...baseTheme.components?.MuiCard,
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: tokens.radii.xl,
            boxShadow: tokens.shadows.base,
            backgroundColor: theme.palette.background.paper,
            transition: 'none', // No transitions for reduced motion
            '&:hover': {
              boxShadow: tokens.shadows.lg,
              transform: 'none', // No transforms for reduced motion
            },
          }),
        },
      },
    };
  }

  return createTheme(baseTheme);
};

/**
 * Cache for theme instances to avoid recreation
 */
const themeCache = new Map<string, Theme>();

/**
 * Utility to get the appropriate theme based on mode and motion preference
 */
export const getTheme = (mode: PaletteMode, reducedMotion: boolean = false): Theme => {
  const cacheKey = `${mode}-${reducedMotion}`;

  if (!themeCache.has(cacheKey)) {
    themeCache.set(cacheKey, createAppTheme(mode, reducedMotion));
  }

  return themeCache.get(cacheKey)!;
};

// Export the tokens for direct usage
export { tokens };

// Export type utilities
export type AppTheme = Theme;
export type ColorMode = PaletteMode;
